import { Inject, Injectable } from '@angular/core';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { Tree } from '../../common/libs';
import { BaseService } from '../../core';
import { City, CommonCode, CountryCallingCode, Province, WorldCountry } from './dto';
import { DisplayCategory } from '../../dto/display-category/display-category.entity';

@Injectable({
  providedIn: 'root',
})
export class CodeService extends BaseService {
  private static commonCodes: { [key: string]: { [key: string]: CommonCode[] } } = {};
  private static commonCodeValues: {
    [key: string]: {
      [key: string]: { [key: string]: CommonCode };
    };
  } = {};
  private static countryCodes: WorldCountry[] = [];
  private static countryCodeValues: {
    [key: string]: WorldCountry;
  } = {};
  private static callingCodes: string[] = [];
  private static callingCodeMap: {
    [key: string]: boolean;
  } = {};
  // private static countryCallingCodes: { [key: string]: CountryCallingCode } = {};
  // private static countryCallingCodes: CountryCallingCode[] = [];

  // private static salesCategories: SalesCategory[] = [];
  // private static salesCategoryValues: { [key: string]: SalesCategory } = {};
  // private static salesCategoryTree: Tree<SalesCategory>;

  // private static displayCategories: DisplayCategory[] = [];
  // private static displayCategoryValues: { [key: string]: DisplayCategory } = {};
  // private static displayCategoryTree: Tree<DisplayCategory>;

  private static displayCategories: {
    [key: string]: DisplayCategory[];
  } = { en: [], vi: [], ko: [] };
  private static displayCategoryValues: {
    [key: string]: { [key: string]: DisplayCategory };
  } = { en: {}, vi: {}, ko: {} };
  private static displayCategoryTree: {
    [key: string]: Tree<DisplayCategory>;
  } = { en: new Tree<DisplayCategory>(null), vi: new Tree<DisplayCategory>(null), ko: new Tree<DisplayCategory>(null) };

  public static FILTERS = {
    includes: <T>(codes: T[], find?: any, value?: (code: T) => string): T[] => {
      if (find === undefined || find === null || find === '') {
        return codes;
      }
      value = value || ((code: T) => code.toString());
      const filterValue = find.toLowerCase();
      return codes.filter((code) => value(code).toLowerCase().includes(filterValue));
    },
  };

  LANGUAGES = [
    { language: 'en', trans: 'LANGUAGE.EN' },
    { language: 'vi', trans: 'LANGUAGE.VI' },
  ];

  constructor(@Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService) {
    super();
  }

  public static getCountryCode(cca2: string) {
    return CodeService.countryCodeValues[cca2];
  }

  public static hasCallingCode(callingCode: string) {
    return CodeService.callingCodeMap[callingCode] ? true : false;
  }

  public static getCountry(cca2: string) {
    const country = CodeService.countryCodeValues[cca2];
    return country ? country.name.common : cca2;
  }

  public getCountryCode(cca2: string) {
    return CodeService.getCountryCode(cca2);
  }

  public getCountry(cca2: string) {
    return CodeService.getCountry(cca2);
  }

  public async initCommonCode() {
    const rawCodes = await this.get<CommonCode[]>('/global/common-code').toPromise();
    const [mainCode, codeValue] = rawCodes.reduce(
      (acc, cur, i) => {
        const [m, v] = acc;
        if (!m[cur.language]) {
          m[cur.language] = {};
        }
        if (!m[cur.language][cur.mainCode]) {
          m[cur.language][cur.mainCode] = [];
        }
        m[cur.language][cur.mainCode].push(cur);

        if (!v[cur.language]) {
          v[cur.language] = {};
        }
        if (!v[cur.language][cur.mainCode]) {
          v[cur.language][cur.mainCode] = {};
        }
        v[cur.language][cur.mainCode][cur.code] = cur;
        return [m, v];
      },
      [{}, {}]
    );

    CodeService.commonCodes = mainCode;
    CodeService.commonCodeValues = codeValue;

    return rawCodes;
  }

  public async initCountryCode() {
    const countryCodes = await this.get<WorldCountry[]>('/country').toPromise();

    CodeService.countryCodes = countryCodes;
    const countryCodeValues = {};
    countryCodes.forEach((country) => {
      countryCodeValues[country.cca2] = country;
    });
    CodeService.countryCodeValues = countryCodeValues;
    return countryCodes;
  }

  /*
  public async initSalesCategory() {
    const salesCategories = await this.get<SalesCategory[]>('/global/sales-category').toPromise();

    CodeService.salesCategories = salesCategories;
    const salesCategoryValues = {};
    salesCategories.forEach(salesCategory => {
      salesCategoryValues[salesCategory.code] = salesCategory;
    });
    CodeService.salesCategoryValues = salesCategoryValues;
    const salesCategoryTree = new Tree<SalesCategory>(null);
    salesCategoryTree.create(salesCategories);
    CodeService.salesCategoryTree = salesCategoryTree;
    console.log('--- CodeService.initSalesCategory : loaded', salesCategories, salesCategoryTree);
    return salesCategoryTree;
  }

  public getSalesCategory() {
    return CodeService.salesCategories;
  }

  public getSalesCategoryAsValue(code: string) {
    return CodeService.salesCategoryValues[code];
  }

  public getSalesCategoryAsTree() {
    return CodeService.salesCategoryTree;
  }

  public initedSalesCategoryAsTree(init: boolean) {
    Object.keys(CodeService.salesCategoryTree.mappedNode).map(key => {
      CodeService.salesCategoryTree.mappedNode[key].selected = init;
      CodeService.salesCategoryTree.mappedNode[key].unfolded = true;
    });
    return CodeService.salesCategoryTree;
  }

  public getSalesCategoryNameAsDepth(code: string) {
    return this.getSalesCategoryAsDepth(code).map(salesCategory => salesCategory.name);
  }

  public getSalesCategoryAsDepth(code: string) {
    let parentCode = code;
    const depth: SalesCategory[] = [];
    if (!parentCode) {
      return depth;
    }

    while (parentCode) {
      const salesCategory = CodeService.salesCategoryValues[parentCode];
      if (!salesCategory) {
        break;
      }
      parentCode = salesCategory.parentCode;
      depth.push(salesCategory);
    }
    return depth.reverse();
  }
  */

  public async initDisplayCategory() {
    const displayCategories = await this.get<DisplayCategory[]>('/global/display-category').toPromise();

    // CodeService.displayCategories = displayCategories;
    displayCategories.forEach((item) => {
      CodeService.displayCategories.en.push(item);
      CodeService.displayCategoryValues.en[item.code] = item;

      CodeService.displayCategories.ko.push(item);
      CodeService.displayCategoryValues.ko[item.code] = item;

      const vi = new DisplayCategory(item);
      vi.name = vi.viName;
      CodeService.displayCategories.vi.push(vi);
      CodeService.displayCategoryValues.vi[vi.code] = vi;
    });

    CodeService.displayCategoryTree.en.create(CodeService.displayCategories.en);
    CodeService.displayCategoryTree.ko.create(CodeService.displayCategories.ko);
    CodeService.displayCategoryTree.vi.create(CodeService.displayCategories.vi);
    return null;
  }

  public getDisplayCategory() {
    return CodeService.displayCategories[this.i18NextService.language];
  }

  public getDisplayCategoryAsValue(code: string) {
    return CodeService.displayCategoryValues[this.i18NextService.language][code];
  }

  public getDisplayCategoryAsTree() {
    return CodeService.displayCategoryTree[this.i18NextService.language];
  }

  public initedDisplayCategoryAsTree(init: boolean) {
    Object.keys(CodeService.displayCategoryTree[this.i18NextService.language].mappedNode).map((key) => {
      CodeService.displayCategoryTree[this.i18NextService.language].mappedNode[key].selected = init;
      CodeService.displayCategoryTree[this.i18NextService.language].mappedNode[key].unfolded = true;
    });
    return CodeService.displayCategoryTree[this.i18NextService.language];
  }

  public getDisplayCategoryNameAsDepth(code: string) {
    return this.getDisplayCategoryAsDepth(code).map((displayCategory) => displayCategory.name);
  }

  public getDisplayCategoryAsDepth(code: string) {
    let parentCode = code;
    const depth: DisplayCategory[] = [];
    if (!parentCode) {
      return depth;
    }

    while (parentCode) {
      const displayCategory = CodeService.displayCategoryValues[this.i18NextService.language][parentCode];
      if (!displayCategory) {
        break;
      }
      parentCode = displayCategory.parentCode;
      depth.push(displayCategory);
    }
    return depth.reverse();
  }

  public async initCallingCode() {
    const countryCallingCodes = await this.get<CountryCallingCode[]>('/country/calling-code').toPromise();
    CodeService.callingCodeMap = countryCallingCodes.reduce((acc, cur, i) => {
      if (!acc[cur.callingCode]) {
        acc[cur.callingCode] = true;
      }
      return acc;
    }, {});

    CodeService.callingCodes = Object.keys(CodeService.callingCodeMap).sort((a, b) => a.localeCompare(b));

    return CodeService.callingCodeMap;
  }

  public getCommonCodes(mainCode: string) {
    return CodeService.commonCodes[this.i18NextService.language][mainCode];
  }

  public getCommonCode(mainCode: string, code: string) {
    return CodeService.commonCodeValues[this.i18NextService.language][mainCode][code];
  }

  public getCountryCodes() {
    return CodeService.countryCodes;
  }

  public getCallingCodes() {
    return CodeService.callingCodes;
  }

  public async getProvince(cca2: string) {
    return await this.get<Province[]>(`/country/${cca2}/province/`).toPromise();
  }

  public async getCityCodes(cca2: string, provinceCode: string) {
    return await this.get<City[]>(`/country/${cca2}/province/${provinceCode}`).toPromise();
  }
}
